import { useMemo, useState } from "react";
import { Box, Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiErrorToast } from "@/hooks/useApiErrorToast";
import { useCatalogoDisparo } from "@/hooks/useCatalogoDisparo";
import {
  DISPARO_QUERY_KEY,
  DISPAROS_QUERY_KEY,
  useDisparo,
  useDisparoHidratado,
  useDisparos,
  useResumoDisparos,
} from "@/hooks/useDisparos";
import {
  CriarDisparoDto,
  DisparoConfiguracao,
  DisparoListItem,
  PreviewBuscaDto,
} from "@/models/consignado/disparo.model";
import {
  ativarDisparo,
  atualizarDisparo,
  criarDisparo,
  previewDisparo,
  removerDisparo,
} from "@/services/consignado/disparo.service";
import DisparoScreen, { type DisparoScreenMode } from "./DisparoScreen";
import DisparosView, { type DisparosFiltros } from "./DisparosView";
import TemplatesContainer from "./TemplatesContainer";
import TesteDisparoModal from "./TesteDisparoModal";

/**
 * Tela de Configuração de Disparos ligada na API real
 * (HIS-APP-notificacoes-007, consignado-trabalhador).
 *
 * Chamadas:
 *  - catálogo: os seis GET /disparos/catalogo/* (useCatalogoDisparo)
 *  - grade:    GET /disparos, paginado e filtrado no servidor
 *  - detalhe:  GET /disparos/:id ao abrir ver/editar — é de onde sai a
 *              `versao` exigida pelo PUT, pelo PATCH e pelo DELETE
 *  - escrita:  POST, PUT /:id, PATCH /:id/ativo, DELETE /:id
 *  - prévia:   POST /disparos/preview (nunca envia mensagem)
 *  - teste:    POST /disparos/:id/executar-agora com `teste`, pela modal do
 *              "Testar template"
 *
 * O único envio daqui é o de teste, para destinos digitados na modal. O
 * "Testar template" grava inativo e só ativa no Confirmar, se a identificação
 * pedir. Ativar revalida o cadastro no backend.
 */
export default function DisparosContainer() {
  const toast = useToast();
  const toastErro = useApiErrorToast();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(20);
  const [filtros, setFiltros] = useState<DisparosFiltros>({});

  // Os templates reutilizáveis moram dentro desta tela, sem rota própria:
  // o botão "Templates" do cabeçalho troca a seção inteira.
  const [secao, setSecao] = useState<"disparos" | "templates">("disparos");
  const [view, setView] = useState<"lista" | "form">("lista");
  const [formMode, setFormMode] = useState<DisparoScreenMode>("create");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // Disparo recém-salvo pelo "Testar template". A modal mora aqui, e não no
  // formulário, porque um disparo novo vira edição ao salvar e o formulário
  // remonta com o detalhe recarregado.
  const [testeDe, setTesteDe] = useState<DisparoConfiguracao | null>(null);
  // O "Testar template" grava sempre inativo: o banco é compartilhado com o
  // cron de dev/hml, que rodaria para o público uma rotina salva ativa antes
  // de o teste chegar. O "Ativo" escolhido na identificação fica guardado
  // aqui e só vale no Confirmar da modal. null = fora do fluxo de teste.
  const [ativoPretendido, setAtivoPretendido] = useState<boolean | null>(null);

  const catalogo = useCatalogoDisparo();
  const lista = useDisparos({ pagina, limite, ...filtros });
  // A faixa de tags acompanha o contexto escolhido no cabeçalho, para contar
  // o mesmo conjunto que a paginação numera no rodapé.
  const resumo = useResumoDisparos(filtros.contexto);
  // Só busca o detalhe quando há um disparo aberto — a grade não traz
  // filtros nem mensagem, então ver/editar precisa do GET por id.
  const detalhe = useDisparo(view === "form" && selectedId ? selectedId : null);
  // Canal gravado só com `templateId` abriria o formulário vazio; o hook
  // resgata o template reutilizável antes de montar o rascunho.
  const hidratado = useDisparoHidratado(detalhe.data);
  // Durante o teste o disparo está inativo no banco, mas o formulário segue
  // mostrando o "Ativo" escolhido — é ele que o Confirmar aplica, e o próximo
  // "Testar template" precisa continuar sabendo dele.
  const disparoDoForm = useMemo(
    () =>
      hidratado.disparo && ativoPretendido !== null
        ? { ...hidratado.disparo, ativo: ativoPretendido }
        : hidratado.disparo,
    [hidratado.disparo, ativoPretendido],
  );

  const invalidarLista = () =>
    queryClient.invalidateQueries({ queryKey: [DISPAROS_QUERY_KEY] });

  const invalidarDetalhe = (id: string) =>
    queryClient.invalidateQueries({ queryKey: [DISPARO_QUERY_KEY, id] });

  const salvar = useMutation({
    mutationFn: async ({ dto, testar }: { dto: CriarDisparoDto; testar: boolean }) => {
      const corpo = testar ? { ...dto, ativo: false } : dto;
      if (formMode === "edit" && selectedId && detalhe.data) {
        // PUT exige a versão que o GET devolveu — é o lock otimista.
        return atualizarDisparo(selectedId, { ...corpo, versao: detalhe.data.versao });
      }
      return criarDisparo(corpo);
    },
    onSuccess: (salvo, { dto, testar }) => {
      toast({
        title: formMode === "edit" ? "Disparo atualizado." : "Disparo criado.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      invalidarLista();
      if (salvo?.id) invalidarDetalhe(salvo.id);
      if (testar && salvo?.id) {
        // Fica no formulário, já em edição: o próximo salvar é PUT deste
        // disparo, e "Editar template" na modal só precisa fechá-la.
        // O detalhe entra no cache com o que o salvar devolveu: sem ele, o
        // formulário dava lugar ao spinner até o GET voltar, e desmontar tira
        // a entrada dele do histórico (useSaidaProtegida) — o "voltar" que
        // isso gera chegava ao formulário remontado e levava para a lista.
        queryClient.setQueryData([DISPARO_QUERY_KEY, salvo.id], salvo);
        setSelectedId(salvo.id);
        setFormMode("edit");
        setAtivoPretendido(!!dto.ativo);
        setTesteDe(salvo);
        return;
      }
      voltarParaLista();
    },
    onError: toastErro(
      "Não foi possível salvar o disparo.",
      "Confira os campos e tente de novo.",
    ),
  });

  const confirmarTeste = useMutation({
    mutationFn: (disparo: DisparoConfiguracao) =>
      ativarDisparo(disparo.id, { ativo: true, versao: disparo.versao }),
    onSuccess: (_, disparo) => {
      toast({ title: "Disparo testado e ativado.", status: "success", duration: 3000, isClosable: true });
      invalidarLista();
      invalidarDetalhe(disparo.id);
      voltarParaLista();
    },
    // O disparo continua salvo, inativo; a modal fica aberta para tentar de novo.
    onError: toastErro(
      "Não foi possível ativar o disparo.",
      "A ativação revalida o cadastro — verifique se está completo.",
    ),
  });

  function voltarParaLista() {
    setTesteDe(null);
    setAtivoPretendido(null);
    setView("lista");
  }

  const alternarAtivo = useMutation({
    mutationFn: ({ item }: { item: DisparoListItem }) =>
      ativarDisparo(item.id, { ativo: !item.ativo, versao: item.versao }),
    onMutate: ({ item }) => setTogglingId(item.id),
    onSuccess: (_, { item }) => {
      invalidarLista();
      invalidarDetalhe(item.id);
    },
    // Ativar revalida o cadastro inteiro no backend: a recusa mais comum
    // aqui é cadastro incompleto, não falha de rede.
    onError: toastErro(
      "Não foi possível alterar o disparo.",
      "A ativação revalida o cadastro — verifique se está completo.",
    ),
    onSettled: () => setTogglingId(null),
  });

  const excluir = useMutation({
    mutationFn: (item: DisparoListItem) => removerDisparo(item.id, item.versao),
    onSuccess: () => {
      toast({ title: "Disparo excluído.", status: "success", duration: 3000, isClosable: true });
      invalidarLista();
    },
    onError: toastErro("Não foi possível excluir o disparo."),
  });

  const abrir = (item: DisparoListItem, modo: DisparoScreenMode) => {
    setSelectedId(item.id);
    setFormMode(modo);
    setAtivoPretendido(null);
    setView("form");
  };

  const handlePreview = (dto: PreviewBuscaDto) => previewDisparo(dto);

  if (secao === "templates") {
    return <TemplatesContainer onVoltar={() => setSecao("disparos")} />;
  }

  if (catalogo.isLoading) {
    return (
      <Flex w="100%" justify="center" py={16}>
        <Spinner color="primary" />
      </Flex>
    );
  }

  if (catalogo.isError) {
    return (
      <Box w="100%" p={{ base: 4, md: 8 }}>
        <Text color="red.500" fontFamily="Lexend Deca">
          Não foi possível carregar o catálogo de disparos. Sem ele o cadastro não pode ser montado.
        </Text>
      </Box>
    );
  }

  if (view === "form") {
    const modalTeste = testeDe && (
      <TesteDisparoModal
        key={`${testeDe.id}-${testeDe.versao}`}
        disparo={testeDe}
        ativaAoConfirmar={!!ativoPretendido}
        confirmando={confirmarTeste.isPending}
        onEditar={() => setTesteDe(null)}
        onConfirmar={() =>
          ativoPretendido ? confirmarTeste.mutate(testeDe) : voltarParaLista()
        }
      />
    );

    // Em ver/editar o formulário só monta com o detalhe em mãos: é dele que
    // vêm filtros, mensagem e a versão. Espera também o resgate do template
    // reutilizável, senão a mensagem apareceria vazia e se preencheria depois.
    if (selectedId && (detalhe.isLoading || hidratado.isLoading)) {
      return (
        <>
          <Flex w="100%" justify="center" py={16}>
            <Spinner color="primary" />
          </Flex>
          {modalTeste}
        </>
      );
    }
    if (selectedId && (detalhe.isError || hidratado.isError)) {
      return (
        <Box w="100%" p={{ base: 4, md: 8 }}>
          <Text color="red.500" fontFamily="Lexend Deca">
            Não foi possível carregar este disparo.
          </Text>
        </Box>
      );
    }

    return (
      <>
        <DisparoScreen
          mode={formMode}
          disparo={selectedId ? disparoDoForm : null}
          catalogo={catalogo.data}
          isSaving={salvar.isPending}
          onBack={voltarParaLista}
          // A promessa trava o Salvar até o POST/PUT responder. A falha já vira
          // toast no onError da mutation — aqui só não pode estourar.
          onSave={(dto) => salvar.mutateAsync({ dto, testar: false }).catch(() => undefined)}
          onSaveAndTest={(dto) => salvar.mutateAsync({ dto, testar: true }).catch(() => undefined)}
          onSwitchToEdit={() => setFormMode("edit")}
          onPreview={handlePreview}
        />
        {modalTeste}
      </>
    );
  }

  return (
    <DisparosView
      items={lista.data?.items ?? []}
      total={lista.data?.total ?? 0}
      totais={resumo.data}
      pagina={pagina}
      limite={limite}
      filtros={filtros}
      catalogo={catalogo.data}
      isLoading={lista.isLoading}
      isError={lista.isError}
      togglingId={togglingId}
      onPaginaChange={setPagina}
      onLimiteChange={(novo) => {
        setLimite(novo);
        setPagina(1);
      }}
      onFiltrosChange={(novos) => {
        setFiltros(novos);
        setPagina(1);
      }}
      onNovo={() => {
        setSelectedId(null);
        setFormMode("create");
        setAtivoPretendido(null);
        setView("form");
      }}
      onTemplates={() => setSecao("templates")}
      onVer={(item) => abrir(item, "view")}
      onEditar={(item) => abrir(item, "edit")}
      onExcluir={(item) => excluir.mutate(item)}
      onToggleAtivo={(item) => alternarAtivo.mutate({ item })}
    />
  );
}
